import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ManualArElement, ManualBody, Vec3 } from "@arc/contracts";
import { createWebglBridge, type WebglBridge, type WebglViewActions } from "./bridge.js";
import { blurActiveElementWithoutCommit } from "./remote-blur.js";
import { readBootstrap } from "../../../tenant/bootstrap.js";

/**
 * Wire the Unity channel to the panel's view state (ADR-049 Phase 1, **RED ZONE**).
 *
 * Every action here mutates the local view or the react-query CACHE. None of them issues a request — that is the
 * invariant D2 states as an effect ("no inbound message may cause a request to leave the browser") and the
 * network-layer test asserts. Writing the cache is deliberate and is not a write: Unity has already persisted the
 * change, and re-fetching to learn what it just told us would be a round trip for nothing.
 */
export interface WebglBridgeWiring {
  /** The peer origin the bootstrap carried, or undefined when the bridge must not arm. */
  bridge: WebglBridge;
}

/**
 * Parse Unity's `"x;y;z"` into the contract's `Vec3` tuple, or `null` when it is not three finite numbers.
 *
 * Unity sends the BACKEND's string form (`WebFrontendBridge.NotifyElementTransformChanged`), but `geometry` is
 * `z.tuple([number, number, number])` NESTED under the element (`manual.contract.ts`) — and `GeometryTable`, the
 * only reader, reads `element.geometry.*`. Spreading the strings at the top level, as the first version did, put
 * off-contract fields on the cached object that nothing read, so an element Unity had just moved kept rendering
 * its old coordinates — the exact redraw this handler exists to perform (diff panel, consensus).
 */
function parseTriple(raw: string | undefined): Vec3 | null {
  if (raw === undefined) return null;
  const parts = raw.split(";").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0]!, parts[1]!, parts[2]!];
}

/** Merge only the axes the message carried — Unity may patch position without touching rotation or size. */
function mergeGeometry(
  current: ManualArElement["geometry"],
  t: { position?: string; rotation?: string; size?: string },
): ManualArElement["geometry"] {
  return {
    position: parseTriple(t.position) ?? current.position,
    rotation: parseTriple(t.rotation) ?? current.rotation,
    size: parseTriple(t.size) ?? current.size,
  };
}

/** One place that knows the body's query key, so the three cache-mirroring actions cannot drift apart. */
function patchBody(
  ctx: { qc: ReturnType<typeof useQueryClient>; realm: string | undefined; sub: string | undefined; manualId: number | null },
  update: (prev: ManualBody) => ManualBody,
): void {
  ctx.qc.setQueryData<ManualBody>(["manual-body", ctx.realm, ctx.sub, ctx.manualId], (prev) => (prev ? update(prev) : prev));
}

export function useWebglBridge(opts: {
  manualId: number | null;
  realm: string | undefined;
  sub: string | undefined;
  body: ManualBody | undefined;
  selectElement: (elementId: number, selectTab: boolean) => void;
}): WebglBridge {
  const qc = useQueryClient();
  const { manualId, realm, sub, body, selectElement } = opts;

  // The peer is stamped by the edge (ADR-049 D1) and validated on read. Absent ⇒ the bridge does not arm.
  const peerOrigin = useMemo(() => readBootstrap()?.unityOrigin, []);

  // Actions are read through a ref so the listener is registered ONCE and never re-bound as the body loads.
  // Re-registering on every render would drop messages that arrive between removal and re-add.
  const latest = useRef({ manualId, realm, sub, body, selectElement, qc });
  latest.current = { manualId, realm, sub, body, selectElement, qc };

  const bridgeRef = useRef<WebglBridge | null>(null);
  /**
   * **Created HERE, not during render, and the cleanup CLEARS the ref** — found live, and the symptom was silence.
   *
   * The previous shape built the bridge lazily in render (`if (bridgeRef.current === null)`) and only disposed it
   * in this cleanup. `StrictMode` runs effects mount → cleanup → mount, so the cleanup removed the `message`
   * listener and the second mount found the ref already populated: nothing re-registered it. `send()` went on
   * working, because it never touches the listener — which is precisely why it hid. Outbound `webglReady` reached
   * the harness while every inbound message was dropped.
   *
   * `main.tsx` renders the panel inside `<StrictMode>`. The double-invoke is development-only, so no deployed
   * build was affected — but the inbound half could not be exercised locally at all, and the dev harness ADR-049
   * D7 exists to provide had never once worked.
   */
  useEffect(() => {
    const actions: WebglViewActions = {
      selectElement: (id, selectTab) => latest.current.selectElement(id, selectTab),

      // BOTH buckets. `locateWebglElement` resolves a target from the step elements AND from `allStepsElements`
      // (the legacy "Alle Schritte" set, ADR-028), so the panel can be displaying one — and a handler that only
      // walked the steps would leave a deleted all-steps element on screen, drop its transform patches, and send
      // `reloadView` down the degrade path for an id that is in fact perfectly visible (diff panel).
      hasElement: (id) => {
        const b = latest.current.body;
        if (!b) return false;
        return (
          b.allStepsElements.some((e) => e.id === id) ||
          b.sections.some((s) => s.steps.some((st) => st.elements.some((e) => e.id === id)))
        );
      },

      // Cache edits, not requests. `setQueryData` is how we mirror what Unity has already saved.
      dropElement: (id) => {
        patchBody(latest.current, (prev) => ({
          ...prev,
          allStepsElements: prev.allStepsElements.filter((e) => e.id !== id),
          sections: prev.sections.map((s) => ({
            ...s,
            steps: s.steps.map((st) => ({ ...st, elements: st.elements.filter((e) => e.id !== id) })),
          })),
        }));
      },

      patchTransform: (id, t) => {
        patchBody(latest.current, (prev) => {
          const apply = (e: ManualArElement): ManualArElement =>
            e.id === id ? { ...e, geometry: mergeGeometry(e.geometry, t), hasBeenPositioned: true } : e;
          return {
            ...prev,
            allStepsElements: prev.allStepsElements.map(apply),
            sections: prev.sections.map((s) => ({
              ...s,
              steps: s.steps.map((st) => ({ ...st, elements: st.elements.map(apply) })),
            })),
          };
        });
      },

      refetchBody: () => {
        const { qc: client, realm: r, sub: u, manualId: m } = latest.current;
        void client.invalidateQueries({ queryKey: ["manual-body", r, u, m] });
      },

      reloadForElement: (id) => {
        // Our OWN url, two params, never a url the peer supplied (ADR-049 D4).
        const url = new URL(window.location.href);
        url.searchParams.set("elementId", String(id));
        url.searchParams.set("elementSelected", "true");
        window.location.href = url.toString();
      },

      blurWithoutCommit: () => blurActiveElementWithoutCommit(),
    };
    bridgeRef.current = createWebglBridge({ peerOrigin, actions });
    return () => {
      bridgeRef.current?.dispose();
      // Clearing is the half that matters: leaving a disposed instance behind is what made the second
      // StrictMode mount a no-op.
      bridgeRef.current = null;
    };
  }, [peerOrigin]);

  /**
   * A STABLE facade over whichever instance is live.
   *
   * Returning the instance itself was the second half of the same bug, and a re-render to swap it in does not fix
   * it: effects that depend on "the bridge" run BEFORE that re-render, against whatever they were handed. The
   * `webglReady` effect is exactly that — it would send through a dead stand-in, set `announcedFor`, and then
   * skip the retry when the real bridge appeared, because the manual id had not changed. Unity would wait forever
   * for a readiness signal, with nothing logged.
   *
   * With a constant identity those effects run once and every call reaches the current instance. `useWebglBridge`
   * is invoked before them, so by the time they run the creation effect has already armed it.
   */
  return useMemo<WebglBridge>(
    () => ({
      send: (message) => bridgeRef.current?.send(message),
      dispose: () => bridgeRef.current?.dispose(),
      get armed() {
        return bridgeRef.current?.armed ?? false;
      },
    }),
    [],
  );
}
